import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { toast } from "react-toastify";
import mesApi from "../../Api/mes.api";
import SearchableSelect from "../DataEntry/SearchableSelect";
import { t } from "../../i18n/locale";
import {
  IngredientAlias,
  PhaseMaterialBasis,
  ProductionPhaseMaterial,
  ProductionPhaseType,
  UnitType,
} from "../../types/mes";

/**
 * Editor dodatnih sirovina (repro/ambalaža) koje proizvod troši u pojedinoj
 * proizvodnoj fazi. Usidreno na (proizvod + tip faze). CRUD ide odmah preko API-ja
 * (neovisno o spremanju faza), pa proizvod mora prethodno biti spremljen.
 */

type Props = {
  productId: number;
  phaseTypes: ProductionPhaseType[];
};

/** Imperativni API: roditelj (product modal "Spremi") može potvrditi nespremljeni red. */
export interface PhaseMaterialEditorHandle {
  /** Sprema ispunjeni "draft" red ako je valjan. Vraća true ako je nešto spremljeno. */
  commitPending: () => Promise<boolean>;
}

const BASIS_OPTIONS: { value: PhaseMaterialBasis; label: string }[] = [
  { value: "per_piece", label: "po komadu" },
  { value: "per_batch", label: "po šarži" },
  { value: "per_kg_dough", label: "po kg tijesta" },
  { value: "baker_pct", label: "baker %" },
];

/**
 * Kolicine se tipkaju i sa zarezom i s tockom (hrvatska tipkovnica / numpad),
 * pa se oba separatora citaju kao decimalna tocka. Prazan unos daje NaN, sto
 * validacija (> 0) odbacuje.
 */
const parseDecimal = (value: number | string | null | undefined): number =>
  value === null || value === undefined || value === "" ? NaN : Number(String(value).replace(",", "."));

type DraftState = {
  PhaseType: number;
  IngredientAlias: number;
  Quantity: string;
  QuantityBasis: PhaseMaterialBasis;
  UnitType: number;
  ScrapPct: string;
};

const emptyDraft = (phaseTypes: ProductionPhaseType[]): DraftState => ({
  PhaseType: phaseTypes[0]?.ID ?? 0,
  IngredientAlias: 0,
  Quantity: "",
  QuantityBasis: "per_piece",
  UnitType: 0,
  ScrapPct: "",
});

const PhaseMaterialEditor = forwardRef<PhaseMaterialEditorHandle, Props>(({ productId, phaseTypes }, ref) => {
  const [items, setItems] = useState<ProductionPhaseMaterial[]>([]);
  const [aliases, setAliases] = useState<IngredientAlias[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [draft, setDraft] = useState<DraftState>(emptyDraft(phaseTypes));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasProduct = productId > 0;

  useEffect(() => {
    // Aliasi (sve sirovine + repro/ambalaža) i jedinice — jednom.
    (async () => {
      try {
        const [al, ut] = await Promise.all([mesApi.getIngredientAliases(), mesApi.getUnitTypes()]);
        setAliases(al);
        setUnitTypes(ut);
      } catch (err: any) {
        console.error("Failed to load aliases/unit-types:", err);
      }
    })();
  }, []);

  useEffect(() => {
    setDraft(emptyDraft(phaseTypes));
  }, [phaseTypes]);

  useEffect(() => {
    if (!hasProduct) {
      setItems([]);
      return;
    }
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await mesApi.getProductPhaseMaterials(productId);
      setItems(data);
    } catch (err: any) {
      toast.error(t("Greška pri učitavanju sirovina po fazi: {message}", { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const aliasOptions = useMemo(
    () =>
      aliases.map((a) => ({
        value: a.ID,
        label: a.AliasName,
        sublabel: a.ExternalCode || a.SourceSystem || undefined,
      })),
    [aliases]
  );

  const phaseTypeName = (id: number) => {
    const pt = phaseTypes.find((p) => p.ID === id);
    return pt?.Name || pt?.Code || t("Faza #{id}", { id });
  };

  /** Je li draft red ispunjen dovoljno za spremanje. */
  const draftIsFilled = () => draft.IngredientAlias > 0 && parseDecimal(draft.Quantity) > 0;

  const persistDraft = async (silent: boolean): Promise<boolean> => {
    if (!hasProduct) return false;
    if (!draft.PhaseType) {
      if (!silent) toast.error(t("Odaberite fazu"));
      return false;
    }
    if (!draft.IngredientAlias) {
      if (!silent) toast.error(t("Odaberite materijal"));
      return false;
    }
    const qty = parseDecimal(draft.Quantity);
    if (!(qty > 0)) {
      if (!silent) toast.error(t("Unesite količinu"));
      return false;
    }

    setSaving(true);
    try {
      await mesApi.createPhaseMaterial({
        Product: productId,
        PhaseType: draft.PhaseType,
        IngredientAlias: draft.IngredientAlias,
        Quantity: qty,
        QuantityBasis: draft.QuantityBasis,
        UnitType: draft.UnitType || null,
        ScrapPct: draft.ScrapPct === "" ? null : Number(draft.ScrapPct),
      });
      if (!silent) toast.success(t("Sirovina dodana u fazu"));
      setDraft(emptyDraft(phaseTypes));
      await loadItems();
      return true;
    } catch (err: any) {
      toast.error(t("Greška pri dodavanju: {message}", { message: err.message }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => persistDraft(false);

  // Roditelj (product modal "Spremi") potvrđuje nespremljeni red da se ništa ne izgubi.
  useImperativeHandle(ref, () => ({
    commitPending: async () => {
      if (!hasProduct || !draftIsFilled()) return false;
      return persistDraft(true);
    },
  }));

  const patchItem = (id: number, patch: Partial<ProductionPhaseMaterial>) => {
    setItems((prev) => prev.map((it) => (it.ID === id ? { ...it, ...patch } : it)));
  };

  const handleSaveRow = async (item: ProductionPhaseMaterial) => {
    const qty = parseDecimal(item.Quantity);
    if (!(qty > 0)) return toast.error(t("Količina mora biti veća od 0"));
    setSaving(true);
    try {
      await mesApi.updatePhaseMaterial(item.ID, {
        PhaseType: item.PhaseType,
        Quantity: qty,
        QuantityBasis: item.QuantityBasis,
        UnitType: item.UnitType || null,
        ScrapPct: item.ScrapPct === "" || item.ScrapPct == null ? null : Number(item.ScrapPct),
      });
      toast.success(t("Spremljeno"));
      await loadItems();
    } catch (err: any) {
      toast.error(t("Greška pri spremanju: {message}", { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      await mesApi.deletePhaseMaterial(id);
      toast.success(t("Uklonjeno"));
      await loadItems();
    } catch (err: any) {
      toast.error(t("Greška pri brisanju: {message}", { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const materialLabel = (item: ProductionPhaseMaterial) =>
    item.IngredientAlias_rel?.Ingredient_IngredientToIngredientAlias?.IngredientName ||
    item.IngredientAlias_rel?.AliasName ||
    `Alias #${item.IngredientAlias}`;

  return (
    <div className="phase-material-editor">
      <div className="phase-editor-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <label style={{ fontWeight: 600 }}>{t("Dodatne sirovine po fazi (repro / ambalaža)")}</label>
      </div>

      {!hasProduct && (
        <div className="empty-state" style={{ padding: 12 }}>
          <p>{t("Spremite proizvod prije dodavanja dodatnih sirovina.")}</p>
        </div>
      )}

      {hasProduct && (
        <>
          <div className="mes-table-wrap">
            <table className="mes-table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>{t("Faza")}</th>
                <th>{t("Materijal")}</th>
                <th style={{ width: 110 }}>{t("Količina")}</th>
                <th style={{ width: 130 }}>{t("Osnova")}</th>
                <th style={{ width: 120 }}>{t("Jedinica")}</th>
                <th style={{ width: 90 }}>{t("Škart %")}</th>
                <th style={{ width: 140 }}>{t("Akcije")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.ID}>
                  <td>
                    <select
                      value={String(item.PhaseType)}
                      onChange={(e) => patchItem(item.ID, { PhaseType: Number(e.target.value) })}
                    >
                      {phaseTypes.map((t) => (
                        <option key={t.ID} value={t.ID}>{t.Name || t.Code}</option>
                      ))}
                    </select>
                  </td>
                  <td>{materialLabel(item)}</td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={String(item.Quantity ?? "")}
                      onChange={(e) => patchItem(item.ID, { Quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      value={item.QuantityBasis}
                      onChange={(e) => patchItem(item.ID, { QuantityBasis: e.target.value as PhaseMaterialBasis })}
                    >
                      {BASIS_OPTIONS.map((b) => (
                        <option key={b.value} value={b.value}>{t(b.label)}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={String(item.UnitType ?? 0)}
                      onChange={(e) => patchItem(item.ID, { UnitType: Number(e.target.value) })}
                    >
                      <option value="0">-</option>
                      {unitTypes.map((u) => (
                        <option key={u.ID} value={u.ID}>{u.Code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.ScrapPct == null ? "" : String(item.ScrapPct)}
                      onChange={(e) => patchItem(item.ID, { ScrapPct: e.target.value })}
                    />
                  </td>
                  <td>
                    <button type="button" className="btn-edit" disabled={saving} onClick={() => handleSaveRow(item)}>
                      {t("Spremi")}
                    </button>
                    <button type="button" className="btn-delete" disabled={saving} onClick={() => handleDelete(item.ID)}>
                      {t("Ukloni")}
                    </button>
                  </td>
                </tr>
              ))}

              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="muted" style={{ textAlign: "center", padding: 10 }}>
                    {t("Nema definiranih dodatnih sirovina.")}
                  </td>
                </tr>
              )}

              {/* Red za dodavanje */}
              <tr>
                <td>
                  <select
                    value={String(draft.PhaseType)}
                    onChange={(e) => setDraft({ ...draft, PhaseType: Number(e.target.value) })}
                  >
                    <option value="0">{t("Odaberi fazu")}</option>
                    {phaseTypes.map((t) => (
                      <option key={t.ID} value={t.ID}>{t.Name || t.Code}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <SearchableSelect
                    value={draft.IngredientAlias}
                    options={aliasOptions}
                    onChange={(v) => setDraft({ ...draft, IngredientAlias: v })}
                    placeholder={t("Pretraži materijal...")}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={draft.Quantity}
                    onChange={(e) => setDraft({ ...draft, Quantity: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    value={draft.QuantityBasis}
                    onChange={(e) => setDraft({ ...draft, QuantityBasis: e.target.value as PhaseMaterialBasis })}
                  >
                    {BASIS_OPTIONS.map((b) => (
                      <option key={b.value} value={b.value}>{t(b.label)}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={String(draft.UnitType)}
                    onChange={(e) => setDraft({ ...draft, UnitType: Number(e.target.value) })}
                  >
                    <option value="0">-</option>
                    {unitTypes.map((u) => (
                      <option key={u.ID} value={u.ID}>{u.Code}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    value={draft.ScrapPct}
                    onChange={(e) => setDraft({ ...draft, ScrapPct: e.target.value })}
                  />
                </td>
                <td>
                  <button type="button" className="btn-submit" disabled={saving} onClick={handleAdd}>
                    {t("+ Dodaj")}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
          <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>
            {t("Osnova količine određuje obračun: „po komadu\" množi s brojem komada šarže, „po šarži\" je fiksno, „po kg tijesta\" množi s masom zamjesa.")}
          </p>
        </>
      )}
    </div>
  );
});

PhaseMaterialEditor.displayName = "PhaseMaterialEditor";

export default PhaseMaterialEditor;
